import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { listCasinos } from "@/data/casinos";
import { FAQ, buildFAQSchema } from "@/components/FAQ";

export function LicensedCasinoPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const casinos = listCasinos({ swedishLicense: true }).sort(
    (a, b) =>
      Number(b.priority_brand) - Number(a.priority_brand) ||
      b.overall_rating - a.overall_rating,
  );

  const title = isSv
    ? "Casino med svensk licens 2026 — kompletta listan"
    : "Casinos with a Swedish license 2026 — the complete list";
  const desc = isSv
    ? "Lista över casinon med svensk spellicens utfärdad av Spelinspektionen. Alla är anslutna till Spelpaus, kräver BankID och följer svensk konsumentlagstiftning."
    : "List of casinos licensed by Spelinspektionen. All are connected to Spelpaus, require BankID and follow Swedish consumer protection rules.";

  const faq = [
    {
      question_sv: "Hur vet jag att ett casino har svensk licens?",
      question_en: "How do I know a casino has a Swedish license?",
      answer_sv:
        "Spelinspektionens svanmärke ska finnas i sidfoten, BankID krävs vid registrering, och operatören ska gå att slå upp i Spelinspektionens publika licenslista.",
      answer_en:
        "Look for Spelinspektionen's swan mark in the footer, BankID at registration, and verify the operator in Spelinspektionen's public license list.",
    },
    {
      question_sv: "Hur många bonusar kan jag få?",
      question_en: "How many bonuses can I get?",
      answer_sv:
        "Endast en välkomstbonus per spelare. Det är en kärnregel i svensk spellicens.",
      answer_en:
        "Only one welcome bonus per player. It is a core rule of the Swedish license.",
    },
    {
      question_sv: "Kan jag stänga av mig från alla licensierade casinon på en gång?",
      question_en: "Can I block myself from every licensed casino at once?",
      answer_sv:
        "Ja, via spelpaus.se. Du loggar in med BankID och avstängningen gäller alla operatörer med svensk licens.",
      answer_en:
        "Yes, through spelpaus.se. You log in with BankID and the block applies to every Swedish-licensed operator.",
    },
    {
      question_sv: "Är vinsterna skattefria?",
      question_en: "Are winnings tax-free?",
      answer_sv:
        "Ja, vinster från svenskt licensierade spelbolag är skattefria för dig som privatperson.",
      answer_en:
        "Yes, winnings from Swedish-licensed operators are tax-free for you as a private individual.",
    },
  ];

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} jsonLd={buildFAQSchema(faq, lang)} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <section className="mt-6 grid gap-6 lg:grid-cols-3">
        <Stat
          number={`${casinos.length}`}
          label={isSv ? "Casinon listade" : "Casinos listed"}
        />
        <Stat number="22%" label={isSv ? "Spelskatt på operatörens vinst" : "Gambling tax on operator GGR"} />
        <Stat
          number="0%"
          label={isSv ? "Skatt för dig som spelare" : "Tax on you as a player"}
        />
      </section>

      <section className="mt-10 prose prose-ink max-w-none">
        <h2>{isSv ? "Vad innebär svensk spellicens?" : "What does a Swedish license mean?"}</h2>
        <p>
          {isSv
            ? "Svensk spellicens utfärdas av Spelinspektionen och krävs för alla operatörer som vill rikta spel mot svenska konsumenter. Licensen är inte en kvalitetsstämpel — den är ett kontrakt: operatören accepterar att följa svensk lag, ansluta sig till Spelpaus och hålla en hög nivå av konsumentskydd."
            : "A Swedish gambling license is issued by Spelinspektionen and required for any operator targeting Swedish consumers. The license is not a quality stamp — it is a contract: the operator accepts Swedish law, connection to Spelpaus and a high standard of consumer protection."}
        </p>
        <h3>{isSv ? "Vad du som spelare får" : "What you get as a player"}</h3>
        <ul>
          <li>{isSv ? "BankID-inloggning på alla licensierade sajter" : "BankID login on every licensed site"}</li>
          <li>{isSv ? "Obligatorisk insättningsgräns vid registrering" : "Mandatory deposit limit at registration"}</li>
          <li>{isSv ? "En bonus per spelare — bonusinflationen är reglerad" : "One bonus per player — bonus inflation is regulated"}</li>
          <li>{isSv ? "Anslutning till Spelpaus — total självavstängning på en knapptryckning" : "Connection to Spelpaus — full self-exclusion in one click"}</li>
          <li>{isSv ? "Skattefria vinster" : "Tax-free winnings"}</li>
          <li>{isSv ? "Tvister går via Allmänna reklamationsnämnden (ARN)" : "Disputes via the National Board for Consumer Disputes (ARN)"}</li>
        </ul>
        <h3>{isSv ? "Trade-offs jämfört med utländska sajter" : "Trade-offs vs offshore sites"}</h3>
        <p>
          {isSv
            ? "Svensk reglering är strikt. Bonusarna är mindre, omsättningskraven granskas hårdare, och vissa internationella titlar är inte tillgängliga. I gengäld får du tydlig konsumenträtt, garanterade vinster och fullt skydd via Spelpaus och Stödlinjen."
            : "Swedish regulation is strict. Bonuses are smaller, wagering requirements are more closely supervised, and some international titles are unavailable. In return you get clear consumer rights, guaranteed payouts and full protection via Spelpaus and Stödlinjen."}
        </p>
      </section>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Vår topplista över svenskt licensierade casinon" : "Our top list of Swedish-licensed casinos"}
      </h2>
      <div className="mt-6 grid gap-4">
        {casinos.map((c, i) => (
          <CasinoCard key={c.id} casino={c} rank={i + 1} source="licensed-list" />
        ))}
      </div>

      <div className="mt-10">
        <FAQ items={faq} />
      </div>

      <InternalLinks context="licensed" />
    </div>
  );
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div className="card p-5">
      <p className="text-3xl font-extrabold gradient-text">{number}</p>
      <p className="mt-1 text-sm text-ink-600">{label}</p>
    </div>
  );
}
